import { airplaneState } from '../airplane/state.js';
import { getAirplaneGroup } from '../airplane/create.js';
import { state } from '../terrain/tiling.js';
import { params } from '../config.js';
import * as SceneModule from '../scene.js';
import * as THREE from 'three';

// Reusable vector for screen position calculations (avoid GC pressure)
const _screenPos = new THREE.Vector3();

// Arc configuration - gentle curve (25 degrees over 90px height)
const gaugeRadius = 100; // Large radius = gentle curve
const arcSpanDegrees = 25; // Total arc span in degrees
const arcSpan = arcSpanDegrees * Math.PI / 180; // ~0.436 radians
const gaugeHeight = 90; // Vertical span in SVG units
const gaugeOffsetX = 75; // Horizontal distance from center
const maxDisplaySpeed = 10.0;

// DOM elements
let curvedGauge, speedArc, speedTrack, speedTick, speedValueText, speedScale;
let altArc, altTrack, altTick, altValueText, altScale;

// Helper: Calculate point on arc given normalized position (0 = bottom, 1 = top)
function getArcPoint(normalizedPos, radius, isLeft) {
  // Map 0-1 to vertical position, with subtle horizontal curve
  // The curve bulges outward (left for speed, right for altitude)
  const y = (0.5 - normalizedPos) * gaugeHeight; // 0->bottom, 1->top
  const curveOffset = Math.sin(normalizedPos * Math.PI) * (gaugeHeight / radius) * 15;

  if (isLeft) {
    const x = -gaugeOffsetX - curveOffset;
    return { x, y };
  } else {
    const x = gaugeOffsetX + curveOffset;
    return { x, y };
  }
}

// Helper: Create SVG path from startPos to endPos (0-1 normalized)
function createArcPath(startPos, endPos, radius, isLeft) {
  const steps = Math.max(2, Math.ceil(Math.abs(endPos - startPos) * 20));
  const points = [];

  for (let i = 0; i <= steps; i++) {
    const pos = startPos + (endPos - startPos) * (i / steps);
    const pt = getArcPoint(pos, radius, isLeft);
    points.push(pt);
  }

  if (points.length === 0) return '';

  let d = `M ${points[0].x.toFixed(2)},${points[0].y.toFixed(2)}`;
  for (let i = 1; i < points.length; i++) {
    d += ` L ${points[i].x.toFixed(2)},${points[i].y.toFixed(2)}`;
  }
  return d;
}

// Create full track path
function createTrackPath(radius, isLeft) {
  return createArcPath(0, 1, radius, isLeft);
}

// Generate scale tick marks along the arcs
function generateScaleTicks(scaleElement, isLeft, numTicks = 11) {
  scaleElement.innerHTML = '';
  // Skip the very last tick (top) for a sleeker look
  for (let i = 0; i < numTicks - 1; i++) {
    const normalizedPos = i / (numTicks - 1);
    const pos = getArcPoint(normalizedPos, gaugeRadius, isLeft);

    // Tick extends perpendicular to the arc (horizontally)
    const tickLength = (i % 5 === 0) ? 6 : 3;
    const tickDir = isLeft ? -1 : 1;

    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', pos.x.toFixed(2));
    line.setAttribute('y1', pos.y.toFixed(2));
    line.setAttribute('x2', (pos.x + tickDir * tickLength).toFixed(2));
    line.setAttribute('y2', pos.y.toFixed(2));

    if (i % 5 === 0) {
      line.classList.add('major');
    }

    scaleElement.appendChild(line);
  }
}

export function updateSpeedUI() {
  if (!speedArc || !speedTick || !speedValueText) return;

  const currentSpeed = state.scrollSpeed;
  const isOverThreshold = currentSpeed > maxDisplaySpeed;

  // Map speed to normalized position (0 = bottom, 1 = top)
  // Allow it to go higher during boost (up to 1.15 visually)
  const maxVisualPercent = 1.15;
  const percent = Math.max(0.05, Math.min(maxVisualPercent, currentSpeed / maxDisplaySpeed));

  // Update arc path (from bottom to current position)
  speedArc.setAttribute('d', createArcPath(0, percent, gaugeRadius, true));

  // Update tick position (horizontal line at current value)
  const tickPos = getArcPoint(percent, gaugeRadius, true);
  const tickLength = 16;
  speedTick.setAttribute('x1', tickPos.x.toFixed(2));
  speedTick.setAttribute('y1', tickPos.y.toFixed(2));
  speedTick.setAttribute('x2', (tickPos.x - tickLength).toFixed(2));
  speedTick.setAttribute('y2', tickPos.y.toFixed(2));

  // Update value text position (to the left of tick)
  speedValueText.setAttribute('x', (tickPos.x - tickLength - 4).toFixed(2));
  speedValueText.setAttribute('y', (tickPos.y + 3).toFixed(2));
  speedValueText.textContent = currentSpeed.toFixed(1);

  // Update colors if over threshold
  if (isOverThreshold) {
    speedArc.style.stroke = '#ff4444';
    speedTick.style.stroke = '#ff4444';
    speedValueText.style.fill = '#ff4444';
    speedTick.style.filter = 'drop-shadow(0 0 4px rgba(255, 68, 68, 0.8))';
  } else {
    speedArc.style.stroke = '';
    speedTick.style.stroke = '';
    speedValueText.style.fill = '';
    speedTick.style.filter = '';
  }
}

export function updateAltitudeUI() {
  if (!altArc || !altTick || !altValueText) return;

  // Get current altitude (baseAltitude + y offset), clamped to minimum of 0
  const currentAlt = Math.max(0, airplaneState.baseAltitude + airplaneState.y);

  // Map altitude to normalized position (0 = bottom, 1 = top)
  const maxAlt = airplaneState.baseAltitude + airplaneState.altitudeRange;
  const percent = Math.max(0.05, Math.min(0.95, currentAlt / maxAlt));

  // Update arc path (from bottom to current position)
  altArc.setAttribute('d', createArcPath(0, percent, gaugeRadius, false));

  // Update tick position (horizontal line at current value)
  const tickPos = getArcPoint(percent, gaugeRadius, false);
  const tickLength = 16;
  altTick.setAttribute('x1', tickPos.x.toFixed(2));
  altTick.setAttribute('y1', tickPos.y.toFixed(2));
  altTick.setAttribute('x2', (tickPos.x + tickLength).toFixed(2));
  altTick.setAttribute('y2', tickPos.y.toFixed(2));

  // Update value text position (to the right of tick)
  altValueText.setAttribute('x', (tickPos.x + tickLength + 4).toFixed(2));
  altValueText.setAttribute('y', (tickPos.y + 3).toFixed(2));
  altValueText.textContent = currentAlt.toFixed(1);
}

// Update gauge position based on airplane screen position
export function updateIndicatorPositions() {
  const { camera, renderer } = SceneModule;
  const airplaneGroup = getAirplaneGroup();
  if (!airplaneGroup || !renderer || !curvedGauge) return;

  // Get canvas dimensions (this is the actual 3D rendering area)
  const canvas = renderer.domElement;
  const rect = canvas.getBoundingClientRect();
  
  // Project airplane position to screen coordinates (using pooled vector)
  _screenPos.copy(airplaneGroup.position);
  _screenPos.project(camera);
  
  // Convert from NDC (-1 to 1) to screen pixels relative to canvas
  const canvasX = (_screenPos.x * 0.5 + 0.5) * rect.width + rect.left;
  const screenY = window.innerHeight / 2; // Keep vertical centered

  // Position the combined gauge centered on the airplane
  curvedGauge.style.left = canvasX + 'px';
  curvedGauge.style.top = screenY + 'px';

  // Apply dynamic scaling based on FOV
  // We want the opposite effect: 1.3 at min FOV (slow), 1.0 at max FOV (fast)
  const currentFov = camera.fov;
  const fovFactor = (currentFov - params.minFov) / (params.maxFov - params.minFov);
  const scale = 1.3 - Math.max(0, Math.min(1, fovFactor)) * 0.3;

  // Update transform - Note: the CSS likely already has translate(-50%, -50%)
  // so we must include it if we're overwriting the transform property.
  // Actually, checking styles... if it's set by left/top, translate(-50%, -50%) is common.
  curvedGauge.style.transform = `translate(-50%, -50%) scale(${scale.toFixed(3)})`;
}

// Initialize curved gauges
export function initCurvedGauges() {
  // Get DOM elements
  curvedGauge = document.getElementById('curved-gauge');
  speedArc = document.getElementById('speed-arc');
  speedTrack = document.getElementById('speed-track');
  speedTick = document.getElementById('speed-tick');
  speedValueText = document.getElementById('speed-value');
  speedScale = document.getElementById('speed-scale');
  altArc = document.getElementById('alt-arc');
  altTrack = document.getElementById('alt-track');
  altTick = document.getElementById('alt-tick');
  altValueText = document.getElementById('alt-value');
  altScale = document.getElementById('alt-scale');

  if (!speedTrack || !altTrack) return;

  // Generate track paths
  speedTrack.setAttribute('d', createTrackPath(gaugeRadius, true));
  altTrack.setAttribute('d', createTrackPath(gaugeRadius, false));

  // Generate scale tick marks
  generateScaleTicks(speedScale, true, 11);
  generateScaleTicks(altScale, false, 11);

  // Initial UI update
  updateSpeedUI();
  updateAltitudeUI();
}
